import React, { useId } from 'react';

const Gauge = ({ value, title, unit = '%' }) => {
    const cleanValue = typeof value === 'number' && !isNaN(value) ? value : 0;
    const percentage = Math.min(Math.max(cleanValue, 0), 100);
    const titleId = useId();

    const getColorClass = () => {
        if (percentage < 70) return 'text-red-500';
        if (percentage < 90) return 'text-orange-500';
        return 'text-green-500';
    };

    const getStatusText = () => {
        if (percentage < 70) return 'Crítico';
        if (percentage < 90) return 'Atenção';
        return 'Bom';
    };

    // SVG parameters
    const strokeWidth = 12;
    // O raio precisa ser a metade da largura do viewBox menos a metade da largura do traço.
    // Largura do viewBox = 128, Largura do traço = 12.
    // Raio = (128 - 12) / 2 = 58.
    const radius = 58;
    const circumference = 2 * Math.PI * radius;
    const arcLength = circumference / 2;

    // Ao usar `stroke-linecap: round`, o arco visível é encurtado pela metade da largura do traço.
    // Para compensar, calculamos um comprimento de arco "estendido" que o preencherá visualmente.
    // Isso faz com que o traço ultrapasse o final matemático do caminho para que a borda arredondada
    // termine exatamente no final do caminho visível.
    const compensation = strokeWidth / 2;
    const compensatedArcLength = arcLength + compensation;

    // Mapeia o percentual para o comprimento do arco compensado
    const strokeOffset = compensatedArcLength - (percentage / 100) * compensatedArcLength;

    const statusText = getStatusText();
    const formattedValue = `${cleanValue.toFixed(0)}${unit}`;
    const accessibleDescription = `${statusText} (${formattedValue})`;

    return (
        <div className="flex flex-col items-center justify-center p-4 bg-slate-100 dark:bg-slate-800/50 rounded-2xl shadow-lg h-full w-full">
            <div
                role="meter"
                aria-labelledby={titleId}
                aria-valuenow={cleanValue}
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuetext={accessibleDescription}
                title={`${title}: ${accessibleDescription}`}
                className="relative w-40 h-20"
            >
                <svg className="w-full h-full" viewBox="0 0 128 64" aria-hidden="true">
                    {/* Background Arc */}
                    <path
                        d={`M ${strokeWidth/2} 64 A ${radius} ${radius} 0 0 1 ${128 - strokeWidth/2} 64`}
                        fill="none"
                        stroke="currentColor"
                        className="text-slate-200 dark:text-slate-700"
                        strokeWidth={strokeWidth}
                    />
                    {/* Foreground Arc */}
                    <path
                        d={`M ${strokeWidth/2} 64 A ${radius} ${radius} 0 0 1 ${128 - strokeWidth/2} 64`}
                        fill="none"
                        stroke="currentColor"
                        className={getColorClass()}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeDasharray={compensatedArcLength}
                        strokeDashoffset={strokeOffset}
                        style={{
                            transition: 'stroke-dashoffset 0.5s ease-in-out',
                        }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-end pb-1" aria-hidden="true">
                     <span className={`text-4xl font-bold ${getColorClass()}`}>
                        {cleanValue.toFixed(0)}
                        <span className="text-2xl text-slate-500 dark:text-slate-400">{unit}</span>
                    </span>
                </div>
            </div>
             <p id={titleId} className="text-sm font-medium text-slate-600 dark:text-slate-300 mt-2">{title}</p>
        </div>
    );
};

export default Gauge;
